"use client";

import { useState } from "react";
import { X, Car, Footprints, Bike, PersonStanding, Train, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type {
  TourRouteSettings,
  AnimationSpeed,
  CameraMode,
  AutoAdvance,
  VehicleStyle,
  TourTravelMode,
} from "./types";

interface TourRouteSettingsProps {
  settings:         TourRouteSettings;
  onSettingsChange: (s: Partial<TourRouteSettings>) => void;
  onClose:          () => void;
  isDark:           boolean;
}

const TRAVEL_MODES: { value: TourTravelMode; label: string; Icon: React.ElementType }[] = [
  { value: "driving",   label: "Drive",   Icon: Car            },
  { value: "walking",   label: "Walk",    Icon: PersonStanding },
  { value: "bicycling", label: "Bike",    Icon: Bike           },
  { value: "transit",   label: "Transit", Icon: Train          },
];

const SPEEDS: { value: AnimationSpeed; label: string; desc: string }[] = [
  { value: "slow",    label: "Slow",    desc: "1 min"  },
  { value: "average", label: "Average", desc: "30 sec" },
  { value: "fast",    label: "Fast",    desc: "8 sec"  },
];

const CAMERAS: { value: CameraMode; label: string; desc: string }[] = [
  { value: "follow",    label: "Follow",    desc: "Locked to vehicle" },
  { value: "cinematic", label: "Cinematic", desc: "Dramatic angles"   },
  { value: "overview",  label: "Overview",  desc: "Wide context view"  },
];

const ADVANCES: { value: AutoAdvance; label: string }[] = [
  { value: "off", label: "Off" },
  { value: 5,     label: "5s"  },
  { value: 10,    label: "10s" },
  { value: 30,    label: "30s" },
];

const VEHICLES: { value: VehicleStyle; label: string; Icon: React.ElementType }[] = [
  { value: "car",     label: "Car",  Icon: Car        },
  { value: "walking", label: "Walk", Icon: Footprints },
  { value: "bike",    label: "Bike", Icon: Bike       },
];

type SectionId = "travel" | "speed" | "camera" | "advance" | "vehicle";

// One collapsible row: click the title to expand it and collapse whichever
// other section was open. The current value stays visible as a summary even
// while collapsed, so the panel is still useful at a glance when compact.
function SettingsSection({
  title, summary, isOpen, onToggle, disabled, head, divider, children,
}: {
  title: string;
  summary: string;
  isOpen: boolean;
  onToggle: () => void;
  disabled?: boolean;
  head: string;
  divider: string;
  children: React.ReactNode;
}) {
  return (
    <div className={disabled ? "opacity-40 pointer-events-none" : ""}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-2.5"
      >
        <span className={`text-[10px] font-black uppercase tracking-widest ${head}`}>{title}</span>
        <span className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-indigo-400">{summary}</span>
          <ChevronDown className={`w-3.5 h-3.5 ${head} transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <div className="pb-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className={`h-px ${divider}`} />
    </div>
  );
}

export default function TourRouteSettings({
  settings, onSettingsChange, onClose,
}: TourRouteSettingsProps) {
  const bg      ="bg-tone-900 border-ink/10 text-ink";
  const head    ="text-tone-500";
  const divider ="bg-ink/8";
  const muted   ="text-tone-400";

  const [openSection, setOpenSection] = useState<SectionId | null>(null);
  const toggleSection = (id: SectionId) =>
    setOpenSection((cur) => (cur === id ? null : id));

  function pill(active: boolean) {
    return active
      ? "bg-indigo-600 border-indigo-500 text-white"
      :"border-ink/10 text-tone-300 hover:bg-ink/10";
  }

  const travelLabel  = TRAVEL_MODES.find(m => m.value === settings.travelMode)?.label ?? "";
  const speedLabel   = SPEEDS.find(s => s.value === settings.speed)?.label ?? "";
  const cameraLabel  = CAMERAS.find(c => c.value === settings.cameraMode)?.label ?? "";
  const advanceLabel = ADVANCES.find(a => a.value === settings.autoAdvance)?.label ?? "";
  const vehicleLabel = VEHICLES.find(v => v.value === settings.vehicleStyle)?.label ?? "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className={`w-72 sm:w-80 rounded-2xl border shadow-2xl backdrop-blur-2xl overflow-hidden flex flex-col ${bg}`}
      style={{ maxHeight: "min(32rem, 70vh)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <span className={`text-[10px] font-black uppercase tracking-widest ${head}`}>
          Tour Settings
        </span>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg transition-colors text-tone-500 hover:text-ink hover:bg-ink/10`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className="px-4 pb-4 overflow-y-auto">

        {/* Routing animation toggle — not collapsible, nothing to hide */}
        <div className="flex items-center justify-between py-2.5">
          <div>
            <p className="text-xs font-semibold">Routing Animation</p>
            <p className={`text-[10px] ${muted}`}>Show car driving between photos</p>
          </div>
          <button
            onClick={() => onSettingsChange({ showRouting: !settings.showRouting })}
            className={`relative w-10 h-5.5 rounded-full transition-colors duration-200 focus:outline-none ${
              settings.showRouting ? "bg-indigo-600" :"bg-ink/15"
            }`}
            style={{ minWidth: "2.5rem", height: "1.375rem" }}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                settings.showRouting ? "translate-x-[1.125rem]" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        <div className={`h-px ${divider}`} />

        <SettingsSection
          title="Camera" summary={cameraLabel} head={head} divider={divider}
          disabled={!settings.showRouting}
          isOpen={openSection === "camera"} onToggle={() => toggleSection("camera")}
        >
          <div className="space-y-1.5">
            {CAMERAS.map(({ value, label, desc }) => (
              <button
                key={value}
                onClick={() => onSettingsChange({ cameraMode: value })}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${pill(settings.cameraMode === value)}`}
              >
                <span>{label}</span>
                <span className={`text-[10px] font-normal ${settings.cameraMode === value ? "text-ink/60" :"text-tone-600"}`}>
                  {desc}
                </span>
              </button>
            ))}
          </div>
        </SettingsSection>

        <SettingsSection
          title="Animation Speed" summary={speedLabel} head={head} divider={divider}
          disabled={!settings.showRouting}
          isOpen={openSection === "speed"} onToggle={() => toggleSection("speed")}
        >
          <div className="grid grid-cols-3 gap-1.5">
            {SPEEDS.map(({ value, label, desc }) => (
              <button
                key={value}
                onClick={() => onSettingsChange({ speed: value })}
                className={`flex flex-col items-center gap-0.5 py-2 rounded-xl border text-[10px] font-bold transition-all ${pill(settings.speed === value)}`}
              >
                <span>{label}</span>
                <span className={`text-[9px] font-normal ${settings.speed === value ? "text-ink/60" :"text-tone-600"}`}>{desc}</span>
              </button>
            ))}
          </div>
        </SettingsSection>

        <SettingsSection
          title="Auto-Advance Photo" summary={advanceLabel} head={head} divider={divider}
          isOpen={openSection === "advance"} onToggle={() => toggleSection("advance")}
        >
          <div className="grid grid-cols-4 gap-1.5">
            {ADVANCES.map(({ value, label }) => (
              <button
                key={String(value)}
                onClick={() => onSettingsChange({ autoAdvance: value })}
                className={`py-2 rounded-xl border text-[10px] font-bold transition-all ${pill(settings.autoAdvance === value)}`}
              >
                {label}
              </button>
            ))}
          </div>
        </SettingsSection>

        <SettingsSection
          title="Travel Mode" summary={travelLabel} head={head} divider={divider}
          disabled={!settings.showRouting}
          isOpen={openSection === "travel"} onToggle={() => toggleSection("travel")}
        >
          <div className="grid grid-cols-4 gap-1.5">
            {TRAVEL_MODES.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => onSettingsChange({ travelMode: value })}
                className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-[10px] font-bold transition-all ${pill(settings.travelMode === value)}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </SettingsSection>

        <SettingsSection
          title="Vehicle" summary={vehicleLabel} head={head} divider={divider}
          disabled={!settings.showRouting}
          isOpen={openSection === "vehicle"} onToggle={() => toggleSection("vehicle")}
        >
          <div className="grid grid-cols-3 gap-1.5">
            {VEHICLES.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => onSettingsChange({ vehicleStyle: value })}
                className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-[10px] font-bold transition-all ${pill(settings.vehicleStyle === value)}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </SettingsSection>

      </div>
    </motion.div>
  );
}
